import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DataTableToolbar, { SortIcon } from "@/components/shared/DataTable/DataTableToolbar";
import DataTablePagination from "@/components/shared/DataTable/DataTablePagination";
import type {
  DataTableColumn,
  DataTableFilterConfig,
  DataTablePaginationMeta,
  DataTableSort,
} from "@/components/shared/types/data-table";

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  getRowId: (row: T) => string;
  title?: string;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  cardClassName?: string;
  /** Controlled search */
  search?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  /** Controlled filters */
  filters?: DataTableFilterConfig[];
  filterValues?: Record<string, string>;
  onFilterChange?: (id: string, value: string) => void;
  /** Controlled sort */
  sort?: DataTableSort;
  onSortChange?: (columnId: string) => void;
  /** Controlled pagination */
  pagination?: DataTablePaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  toolbarExtra?: ReactNode;
  /** Override default mobile card rendering */
  renderMobileCard?: (row: T) => ReactNode;
  /** responsive = table on desktop + cards on mobile; cards = cards everywhere; table = table everywhere */
  viewMode?: "responsive" | "cards" | "table";
  hidePagination?: boolean;
}

function getCellValue<T>(row: T, column: DataTableColumn<T>): ReactNode {
  if (column.cell) return column.cell(row);
  if (column.accessorKey) return String(row[column.accessorKey] ?? "—");
  return "—";
}

function DefaultMobileCard<T>({ row, columns }: { row: T; columns: DataTableColumn<T>[] }) {
  const visibleColumns = columns
    .filter((col) => !col.hideOnMobile)
    .sort((a, b) => (a.mobilePriority ?? 99) - (b.mobilePriority ?? 99));

  const primary = visibleColumns[0];
  const rest = visibleColumns.slice(1);

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      {primary && (
        <div className="font-semibold text-sm">{getCellValue(row, primary)}</div>
      )}
      <div className="grid gap-2">
        {rest.map((column) => (
          <div key={column.id} className="flex items-start justify-between gap-3 text-sm">
            <span className="text-muted-foreground shrink-0">{column.mobileLabel ?? column.header}</span>
            <span className="text-right font-medium">{getCellValue(row, column)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {Array.from({ length: columns }).map((__, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton className="h-4 w-full max-w-[120px]" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function DataTable<T>({
  columns,
  data,
  getRowId,
  title,
  loading = false,
  emptyMessage = "No results found.",
  className,
  cardClassName,
  search,
  searchPlaceholder = "Search...",
  onSearchChange,
  filters,
  filterValues,
  onFilterChange,
  sort,
  onSortChange,
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  toolbarExtra,
  renderMobileCard,
  viewMode = "responsive",
  hidePagination = false,
}: DataTableProps<T>) {
  const alignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  } as const;

  const showCards = viewMode === "cards" || viewMode === "responsive";
  const showTable = viewMode === "table" || viewMode === "responsive";
  const cardsClass = viewMode === "cards" ? "space-y-3 p-4" : "space-y-3 p-4 lg:hidden";
  const tableClass = viewMode === "table" ? "block" : "hidden lg:block";

  return (
    <Card className={cn(cardClassName)}>
      <DataTableToolbar
        title={title}
        search={search}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={onSearchChange}
        filters={filters}
        filterValues={filterValues}
        onFilterChange={onFilterChange}
        toolbarExtra={toolbarExtra}
      />

      <CardContent className={cn("p-0", className)}>
        {/* Mobile / card list */}
        {showCards && (
        <div className={cardsClass}>
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-28 w-full rounded-lg" />
            ))
          ) : data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
          ) : (
            data.map((row) => (
              <div key={getRowId(row)}>
                {renderMobileCard ? renderMobileCard(row) : <DefaultMobileCard row={row} columns={columns} />}
              </div>
            ))
          )}
        </div>
        )}

        {/* Desktop table */}
        {showTable && (
        <div className={tableClass}>
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead
                    key={column.id}
                    className={cn(alignClass[column.align ?? "left"], column.headerClassName)}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        className="inline-flex items-center font-medium hover:text-foreground"
                        onClick={() => onSortChange(column.id)}
                      >
                        {column.header}
                        <SortIcon sort={sort} columnId={column.id} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <LoadingRows columns={columns.length} />
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={getRowId(row)}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={cn(alignClass[column.align ?? "left"], column.className)}
                      >
                        {getCellValue(row, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        )}

        {!hidePagination && pagination && onPageChange && onPageSizeChange && (
          <DataTablePagination
            pagination={pagination}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={pageSizeOptions}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default DataTable;
